import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { saveEmailLead } from '../../actions/signupAction';
import { isValidEmail } from '../../js/helper';
import { FormattedMessage } from 'react-intl';

const FEATURES = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
    title: 'Board & List views',
    desc: 'Kanban boards, list views, calendars, and custom fields all in one place.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Real-time collaboration',
    desc: 'Assign tasks, mention teammates, and keep everyone in sync with live updates.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: 'Dashboards & Reports',
    desc: 'Track progress, spot bottlenecks, and make decisions faster with live dashboards.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    title: 'Portfolios & Goals',
    desc: 'Group related projects into portfolios and track high-level goals across your workspace.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
      </svg>
    ),
    title: 'Task filters & custom fields',
    desc: 'Build workflows your way with powerful filters, custom fields, and saved views.',
  },
];

const SOCIAL_PROOF = [
  // { label: 'Teams using Workflo', value: '10,000+' },
  // { label: 'Uptime', value: '99.9%' },
  // { label: 'Support', value: '24/7' },
];

const GetStarted = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = async () => {
    if (email) {
      if (isValidEmail(email)) {
        setError(false);
        dispatch(saveEmailLead(email));
        navigate(`/signup?email=${email}`, { replace: true });
      } else {
        setError(true);
      }
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: 'inherit', background: 'linear-gradient(135deg, #0f0c29, #1a1a4e, #24243e)', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes orb-float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(60px, -80px) scale(1.1); }
          66% { transform: translate(-40px, 40px) scale(0.95); }
        }
        @keyframes orb-float-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-70px, 50px) scale(1.05); }
          66% { transform: translate(50px, -60px) scale(0.9); }
        }
        @keyframes orb-float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(40px, 70px) scale(1.08); }
        }
        @keyframes grid-pulse {
          0%, 100% { opacity: 0.03; }
          50% { opacity: 0.07; }
        }
        .gs-orb-1 { animation: orb-float-1 12s ease-in-out infinite; }
        .gs-orb-2 { animation: orb-float-2 16s ease-in-out infinite; }
        .gs-orb-3 { animation: orb-float-3 10s ease-in-out infinite; }
        .gs-grid { animation: grid-pulse 6s ease-in-out infinite; }
        .gs-btn-primary {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          box-shadow: 0 4px 20px rgba(99,102,241,0.4);
          transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
        }
        .gs-btn-primary:hover:not(:disabled) {
          filter: brightness(1.12);
          box-shadow: 0 6px 28px rgba(99,102,241,0.55);
          transform: translateY(-1px);
        }
        .gs-btn-primary:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      `}</style>

      {/* Animated background */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div className="gs-orb-1" style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', top: -150, left: -100, background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)' }} />
        <div className="gs-orb-2" style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', bottom: -100, right: -80, background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)' }} />
        <div className="gs-orb-3" style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', top: '40%', left: '55%', background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)' }} />
        <div className="gs-grid" style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
      </div>

      {/* ── Left panel ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 64px', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 480 }}>
          {/* Logo */}
          <img
            src="/images/icons/logo.svg"
            style={{ height: 26, marginBottom: 48, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
            alt="Workflo"
            onError={e => { e.target.style.display = 'none'; }}
          />

          {/* Headline */}
          <h1 style={{ fontSize: 38, fontWeight: 800, color: 'rgba(255,255,255,0.95)', margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
            The workspace your<br />team will actually use.
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', margin: '0 0 48px', lineHeight: 1.6 }}>
            Plan, track, and ship work — all in one place. Free forever for teams getting started.
          </p>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 52 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#a5b4fc' }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 2 }}>{f.title}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Social proof */}
          {/* <div style={{ display: 'flex', gap: 32, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {SOCIAL_PROOF.map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'rgba(255,255,255,0.9)', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div> */}
        </div>
      </div>

      {/* ── Right panel ─────────────────────────────────────────────── */}
      <div style={{ width: 480, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 48px', position: 'relative', zIndex: 1, minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: 360, borderRadius: 20, padding: '40px 36px', background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'rgba(255,255,255,0.95)', margin: '0 0 6px' }}>
              Create your account
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
              Free forever. No credit card required.
            </p>
          </div>

          {/* Email */}
          <div style={{ marginBottom: 6 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Work email
            </label>
            <input
              type="email"
              value={email}
              autoFocus
              placeholder="you@company.com"
              onChange={(e) => { setEmail(e.target.value); setError(false); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: 14,
                border: error ? '1.5px solid rgba(239,68,68,0.7)' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 10, outline: 'none',
                background: 'rgba(255,255,255,0.07)',
                color: 'rgba(255,255,255,0.9)',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => { if (!error) e.target.style.borderColor = 'rgba(99,102,241,0.6)'; }}
              onBlur={e => { if (!error) e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}
            />
          </div>

          {error && (
            <p style={{ fontSize: 12, color: 'rgba(239,68,68,0.85)', margin: '4px 0 12px' }}>
              Please enter a valid email address.
            </p>
          )}

          {/* Continue */}
          <button
            onClick={handleSubmit}
            disabled={!email}
            className="gs-btn-primary"
            style={{
              width: '100%', marginTop: 16, padding: '12px 0', fontSize: 14, fontWeight: 600,
              borderRadius: 10, border: 'none', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <FormattedMessage id="CONTINUE" defaultMessage="Continue" />
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>or continue with</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
          </div>

          {/* Google / Apple SSO */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
            {[
              { logo: '/images/icons/google-logo.svg', label: 'Google' },
              { logo: '/images/icons/apple-logo.svg', label: 'Apple' },
            ].map(({ logo, label }) => (
              <button
                key={label}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 0', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, background: 'rgba(255,255,255,0.07)', fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                onClick={() => navigate('/signup')}
              >
                <img src={logo} style={{ height: 16, width: 16, objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.8 }} alt={label} />
                {label}
              </button>
            ))}
          </div>

          {/* Sign in */}
          <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
            Already have an account?{' '}
            <button
              onClick={() => navigate('/signin')}
              style={{ background: 'none', border: 'none', padding: 0, color: '#818cf8', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color = '#a5b4fc'}
              onMouseLeave={e => e.currentTarget.style.color = '#818cf8'}
            >
              Sign in
            </button>
          </p>

          {/* Trust */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {['No Card Required', 'Mobile app'].map((t) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 8l3.5 3.5 7.5-7" />
                </svg>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
